import type { ITableLikeTreeItem } from '../context';

export const getAllParentsItems = (item: ITableLikeTreeItem, acc: ITableLikeTreeItem[] = []): ITableLikeTreeItem[] => {
    if (!item?.parent) {
        return acc;
    }
    acc.push(item.parent);

    return getAllParentsItems(item.parent, acc);
};

export const getAllChildren = (item: ITableLikeTreeItem, acc: ITableLikeTreeItem[] = []) => {
    const childrenLen = item?.children?.length;

    if (childrenLen) {
        acc = [...acc, ...(item?.children || [])];

        for (let i = 0; i < childrenLen; i++) {
            const child = item?.children?.[i];

            if (child) {
                acc = getAllChildren(child, acc);
            }
        }
    }

    return acc;
};

export const getLeafChildren = (item: ITableLikeTreeItem): ITableLikeTreeItem[] => {
    const children = item?.children;

    if (!children?.length) {
        return [];
    }

    return children.flatMap((child) => (child.children?.length ? getLeafChildren(child) : [child]));
};

type TItemWithGuaranteedVolume = ITableLikeTreeItem & { hasGuaranteedVolume?: boolean };

export const getGuaranteedVolumeCheckboxState = (item: TItemWithGuaranteedVolume) => {
    const leaves = getLeafChildren(item) as TItemWithGuaranteedVolume[];
    const hasGuaranteedVolume = Boolean(item.hasGuaranteedVolume);

    if (!leaves.length) {
        return {
            checked: hasGuaranteedVolume,
            intermediate: false,
        };
    }

    const allLeavesGuaranteed = leaves.every((leaf) => Boolean(leaf.hasGuaranteedVolume));
    const someLeavesGuaranteed = leaves.some((leaf) => Boolean(leaf.hasGuaranteedVolume));

    if (allLeavesGuaranteed) {
        return {
            checked: true,
            intermediate: false,
        };
    }

    if (hasGuaranteedVolume || someLeavesGuaranteed) {
        return {
            checked: true,
            intermediate: true,
        };
    }

    return {
        checked: false,
        intermediate: false,
    };
};

export const getRootItem = (item: ITableLikeTreeItem): ITableLikeTreeItem => {
    if (!item?.parent) {
        return item;
    }

    return getRootItem(item.parent);
};

const treeHierarchyToFlat = (
    item: ITableLikeTreeItem,
    acc: ITableLikeTreeItem[],
    level = 0,
    parent?: ITableLikeTreeItem,
    index?: number,
    sameLevelItems?: ITableLikeTreeItem[],
) => {
    const sameLevelLastIdx = !sameLevelItems ? 0 : sameLevelItems.length - 1;

    item.isFirstChild = !index;
    item.isLastChild = (index || 0) === sameLevelLastIdx;
    item.treeLevel = level;
    item.parent = parent;
    acc.push(item);

    const childrenLen = item?.children?.length;

    if (childrenLen) {
        for (let i = 0; i < childrenLen; i++) {
            const child = item?.children?.[i];

            if (child) {
                treeHierarchyToFlat(child, acc, level + 1, item, i, item?.children);
            }
        }
    }
};

export const getTableTreeRows = (data: ITableLikeTreeItem[]): ITableLikeTreeItem[] =>
    data.reduce((acc: ITableLikeTreeItem[], item, index, array) => {
        treeHierarchyToFlat(item, acc, 0, undefined, index, array);

        return acc;
    }, []);

const clearSource = (items: ITableLikeTreeItem[], source: ITableLikeTreeItem) => {
    const { id: sourceId, parent: sourceParent } = source;
    const sourceChildren = getAllChildren(source);
    const sourceIndex = items.findIndex((item) => item.id === sourceId);

    if (sourceParent) {
        sourceParent.children = sourceParent?.children?.filter((c) => c.id !== sourceId);
        const sourceParentIndex = items.findIndex((item) => item.id === sourceParent.id);

        if (sourceParentIndex !== -1) {
            items.splice(sourceParentIndex, 1, { ...sourceParent });
        }
    }

    if (sourceIndex !== -1) {
        items.splice(sourceIndex, sourceChildren.length + 1);
    }
};

export const appendChild = (
    flatItems: ITableLikeTreeItem[],
    source: ITableLikeTreeItem,
    target: ITableLikeTreeItem,
) => {
    const targetTmp = flatItems.find((item) => item.id === target.id);
    const sourceTmp = flatItems.find((item) => item.id === source.id) || source;

    if (targetTmp && sourceTmp) {
        targetTmp.children = [...(targetTmp.children || []).filter((c) => c.id !== sourceTmp.id), sourceTmp];

        if (sourceTmp.parent && sourceTmp.parent.id !== targetTmp.id) {
            sourceTmp.parent.children = sourceTmp.parent.children?.filter((c) => c.id !== sourceTmp.id);
        }

        sourceTmp.parent = targetTmp;
    }

    return getTableTreeRows(flatItems.filter((i) => !i.parent));
};

export const insertChild = (
    flatItems: ITableLikeTreeItem[],
    source: ITableLikeTreeItem,
    target: ITableLikeTreeItem,
    isBefore?: boolean,
) => {
    const targetTmp = flatItems.find((item) => item.id === target.id);
    const sourceTmp = flatItems.find((item) => item.id === source.id);
    let rootItems = flatItems.filter((i) => !i.parent);

    if (targetTmp && sourceTmp) {
        if (sourceTmp.parent) {
            sourceTmp.parent.children = sourceTmp.parent.children?.filter((c) => c.id !== sourceTmp.id);
        } else {
            rootItems = rootItems.filter((c) => c.id !== sourceTmp.id);
        }

        let insertToCollection = rootItems;

        if (target.parent) {
            const { parent } = target;

            sourceTmp.parent = parent;

            if (!parent.children) {
                parent.children = [];
            }

            insertToCollection = parent.children;
        }

        let insertIndex = insertToCollection.findIndex((c) => c.id === target.id);

        if (insertIndex !== -1) {
            insertIndex = isBefore ? insertIndex : insertIndex + 1;

            insertToCollection.splice(insertIndex, 0, sourceTmp);

            return getTableTreeRows(rootItems);
        }
    }

    return flatItems;
};

export const deleteItem = (flatItems: ITableLikeTreeItem[], item: ITableLikeTreeItem) => {
    let itemsTmp = [...flatItems];

    clearSource(itemsTmp, item);

    itemsTmp = getTableTreeRows(itemsTmp.filter((i) => !i.parent));

    return itemsTmp;
};

export const replaceItem = (
    flatItems: ITableLikeTreeItem[],
    item: ITableLikeTreeItem,
    replaceByItem: ITableLikeTreeItem,
) => {
    let itemsTmp = [...flatItems];

    const replaceIndex = itemsTmp.findIndex((c) => c.id === item.id);
    const indexAsChild = item.parent?.children?.findIndex((c) => c.id === item.id);

    clearSource(itemsTmp, item);

    replaceByItem.parent = item.parent;

    if (replaceByItem.parent && Number.isFinite(indexAsChild) && indexAsChild !== -1) {
        replaceByItem.parent?.children?.splice(indexAsChild || 0, 0, replaceByItem);
    }

    if (replaceIndex !== -1) {
        itemsTmp.splice(replaceIndex, 0, replaceByItem);
    }

    itemsTmp = getTableTreeRows(itemsTmp.filter((i) => !i.parent));

    return itemsTmp;
};

export const addChild = (flatItems: ITableLikeTreeItem[], child: ITableLikeTreeItem, parent?: ITableLikeTreeItem) => {
    if (parent) {
        return appendChild(flatItems, child, parent);
    }

    const itemsTmp = [...flatItems];
    const result: ITableLikeTreeItem[] = [];

    treeHierarchyToFlat(child, result, 0, undefined, itemsTmp.length ? itemsTmp.length - 1 : 0, itemsTmp);

    return itemsTmp.concat(...result);
};

export const getInsertAfterObjectId = (
    rows: ITableLikeTreeItem[],
    source: ITableLikeTreeItem,
    target: ITableLikeTreeItem,
    isInsert?: boolean,
    isBefore?: boolean,
) => {
    let insertAfterObjectId: number | undefined;

    if (isInsert) {
        if (isBefore) {
            let parentChildren = target.parent ? target.parent.children || [] : rows.filter((r) => !r.parent);

            parentChildren = parentChildren.filter((c) => c.id !== source.id);
            const index = parentChildren.findIndex((c) => c.id === target.id);

            if (index === -1 || index >= 1) {
                insertAfterObjectId = parentChildren[index - 1].id;
            }
        } else {
            insertAfterObjectId = target.id;
        }
    } else {
        const targetChildren = (target.children || []).filter((c) => c.id !== source.id);

        insertAfterObjectId = targetChildren[targetChildren.length ? targetChildren.length - 1 : 0]?.id;
    }

    return insertAfterObjectId;
};
